import React from 'react';
import {
  Alert,
  Button,
  Content,
  Flex,
  FlexItem,
  Label,
  Stack,
  StackItem,
  Title,
  Tooltip,
} from '@patternfly/react-core';
import { Table, Tbody, Td, Th, Thead, Tr } from '@patternfly/react-table';
import { PencilAltIcon, PlusCircleIcon, TrashIcon } from '@patternfly/react-icons';
import type {
  GpuBudget,
  GpuBudgetCatalog,
  GpuBudgetCatalogKind,
  GpuBudgetLine,
  GpuBudgetWarning,
} from '../../../../types/models/GpuBudget';
import { formatPrice } from '../../../../utils/money';
import { ProductDescription } from '../Start/ServerStep';
import {
  catalogKind,
  estimateNoteFor,
  formatBasisPrice,
  formatCount,
  formatQuantity,
  interpolateCopy,
  isLineOk,
  kindCopy,
  lineName,
  lineStatusLabel,
  lineWarning,
  sectionWarning,
  warningStack,
} from './budgetLineKinds';
import { BUDGET_OTHER_ITEMS_NOTE, BUDGET_OTHER_ITEMS_TITLE, BUDGET_REMOVED_PRODUCT } from './gpuBudgetCopy';
import GpuBudgetTotalsCard from './GpuBudgetTotalsCard';

interface GpuBudgetWarningStackProps {
  /** `null` while no budget is selected: the catalog's warnings alone. */
  budget: GpuBudget | null;
  catalog: GpuBudgetCatalog;
}

/** The deduplicated warnings stack at the top of the tab, verbatim backend messages. */
export const GpuBudgetWarningStack: React.FunctionComponent<GpuBudgetWarningStackProps> = ({ budget, catalog }) => {
  const warnings = warningStack(budget, catalog);
  if (warnings.length === 0) {
    return null;
  }
  return (
    <Stack hasGutter className="pf-v6-u-mb-md">
      {warnings.map((warning) => (
        <StackItem key={warning.code}>
          <Alert
            variant={warning.code === 'storage_not_orderable' ? 'info' : 'warning'}
            isInline
            title={warning.message}
          />
        </StackItem>
      ))}
    </Stack>
  );
};

interface GpuBudgetEditorProps {
  budget: GpuBudget;
  catalog: GpuBudgetCatalog;
  /** A write is pending: the row and section actions are disabled meanwhile. */
  isBusy: boolean;
  onAddLine: (kind: GpuBudgetCatalogKind) => void;
  onEditLine: (kind: GpuBudgetCatalogKind, line: GpuBudgetLine) => void;
  onRemoveLine: (line: GpuBudgetLine) => void;
}

/** A muted product cell: the label (or "Removed product"), its description, and a status label. */
const ProductCell: React.FunctionComponent<{ line: GpuBudgetLine; warning: GpuBudgetWarning | null }> = ({
  line,
  warning,
}) => {
  const status = lineStatusLabel(line);
  return (
    <>
      {line.product !== null ? (
        <div>{line.product.label}</div>
      ) : (
        <div className="pf-v6-u-text-color-subtle">{BUDGET_REMOVED_PRODUCT}</div>
      )}
      <ProductDescription description={line.product?.description ?? null} />
      {status !== null && (
        <div className="pf-v6-u-mt-xs">
          <Label isCompact color={line.status === 'price_unavailable' ? 'grey' : 'orange'}>
            {status}
          </Label>
          {warning !== null && (
            <div className="pf-v6-u-font-size-sm pf-v6-u-text-color-subtle pf-v6-u-mt-xs">{warning.message}</div>
          )}
        </div>
      )}
    </>
  );
};

/** An amount cell: the backend's string, or "—" while the line is not `ok`. */
const money = (value: string | null): string => (value !== null ? formatPrice(value) : '—');

/** An icon-only row action. */
const RowAction: React.FunctionComponent<{
  label: string;
  icon: React.ReactNode;
  onClick: () => void;
  isDisabled: boolean;
}> = ({ label, icon, onClick, isDisabled }) => (
  <Button variant="plain" aria-label={label} icon={icon} onClick={onClick} isDisabled={isDisabled} />
);

/**
 * The editor of the selected budget, driven by the backend catalog: one section per `catalog.kinds[]`
 * in catalog order (the copy registry only adds nicer wording — a kind without an entry renders with
 * the generic fallback and stays editable), then an "Other items" section for lines of a kind the
 * catalog no longer offers (Remove only), then the totals card.
 *
 * Every amount is a backend string, formatted only — nothing is computed here. A line that is not
 * `ok` is muted, labelled ("No longer available" / "Price not available"), shows "—" amounts and its
 * own warning; Edit and Remove stay available.
 *
 * Compact tables with no search and no pagination — an explicit opt-out from the list-table rule
 * (Bruno, 2026-09-29): a budget holds at most `catalog.limits.max_lines` lines, split by kind.
 */
const GpuBudgetEditor: React.FunctionComponent<GpuBudgetEditorProps> = ({
  budget,
  catalog,
  isBusy,
  onAddLine,
  onEditLine,
  onRemoveLine,
}) => {
  const atLineLimit = budget.lines.length >= catalog.limits.max_lines;
  const otherLines = budget.lines.filter((line) => catalogKind(catalog, line.kind) === null);

  const renderAddButton = (kind: GpuBudgetCatalogKind) => {
    const copy = kindCopy(kind.kind);
    const disabledReason =
      kind.products.length === 0
        ? copy.noProductsTooltip
        : atLineLimit
          ? `This budget already holds the maximum of ${formatCount(catalog.limits.max_lines)} lines.`
          : null;
    const button = (
      <Button
        variant="secondary"
        icon={<PlusCircleIcon />}
        onClick={() => onAddLine(kind)}
        isAriaDisabled={disabledReason !== null || isBusy}
      >
        {copy.addLabel}
      </Button>
    );
    return disabledReason !== null ? <Tooltip content={disabledReason}>{button}</Tooltip> : button;
  };

  const renderSection = (kind: GpuBudgetCatalogKind) => {
    const copy = kindCopy(kind.kind);
    const lines = budget.lines.filter((line) => line.kind === kind.kind);
    const hasHours = kind.hours_per_month !== null;
    const inSectionWarning = sectionWarning(budget, copy.sectionWarningCodes);
    const estimateNote = estimateNoteFor(kind, catalog);
    const unitHeader = `${copy.isKnown ? 'Price' : 'Unit price'} excl. VAT`;

    return (
      <section key={kind.kind} className="pf-v6-u-mb-xl" aria-labelledby={`gpu-budget-section-${kind.kind}`}>
        <Flex
          justifyContent={{ default: 'justifyContentSpaceBetween' }}
          alignItems={{ default: 'alignItemsCenter' }}
          className="pf-v6-u-mb-sm"
        >
          <FlexItem>
            <Title headingLevel="h3" size="lg" id={`gpu-budget-section-${kind.kind}`}>
              {copy.title}
            </Title>
          </FlexItem>
          <FlexItem>{renderAddButton(kind)}</FlexItem>
        </Flex>
        {copy.note !== null && (
          <Content component="p" className="pf-v6-u-text-color-subtle pf-v6-u-mb-sm">
            {interpolateCopy(copy.note, catalog)}
          </Content>
        )}
        {inSectionWarning !== null ? (
          <Alert variant="info" isInline isPlain title={inSectionWarning.message} className="pf-v6-u-mb-sm" />
        ) : (
          estimateNote !== null && (
            <Alert variant="info" isInline isPlain title={estimateNote} className="pf-v6-u-mb-sm" />
          )
        )}
        {lines.length === 0 ? (
          <Content component="p" className="pf-v6-u-text-color-subtle">
            {copy.emptyText}
          </Content>
        ) : (
          <Table aria-label={`${copy.title} of the budget`} variant="compact">
            <Thead>
              <Tr>
                <Th>{copy.productHeader}</Th>
                <Th modifier="nowrap">{copy.quantityHeader}</Th>
                {hasHours && <Th modifier="nowrap">Hours / month</Th>}
                <Th modifier="nowrap">{unitHeader}</Th>
                <Th modifier="nowrap">Monthly excl. VAT</Th>
                <Th modifier="nowrap">Monthly incl. VAT</Th>
                <Th screenReaderText="Actions" />
              </Tr>
            </Thead>
            <Tbody>
              {lines.map((line) => {
                const ok = isLineOk(line);
                const muted = ok ? undefined : 'pf-v6-u-text-color-subtle';
                const name = lineName(line);
                return (
                  <Tr key={line.id}>
                    <Td dataLabel={copy.productHeader} className={muted}>
                      <ProductCell line={line} warning={lineWarning(budget, line)} />
                    </Td>
                    <Td dataLabel={copy.quantityHeader} modifier="nowrap" className={muted}>
                      {copy.isKnown ? formatCount(line.quantity) : formatQuantity(kind.quantity.unit, line.quantity)}
                    </Td>
                    {hasHours && (
                      <Td dataLabel="Hours / month" modifier="nowrap" className={muted}>
                        {line.hours_per_month !== null ? `${formatCount(line.hours_per_month)} h` : '—'}
                      </Td>
                    )}
                    <Td dataLabel={unitHeader} modifier="nowrap" className={muted}>
                      {ok && line.unit_price_ht !== null
                        ? formatBasisPrice(line.unit_price_ht, line.unit_price_basis)
                        : '—'}
                    </Td>
                    <Td dataLabel="Monthly excl. VAT" modifier="nowrap" className={muted}>
                      {ok ? money(line.monthly_ht) : '—'}
                    </Td>
                    <Td dataLabel="Monthly incl. VAT" modifier="nowrap" className={muted}>
                      {ok ? money(line.monthly_ttc) : '—'}
                      {ok && line.quantity > 1 && line.per_server !== null && (
                        <div className="pf-v6-u-font-size-sm pf-v6-u-text-color-subtle">
                          per server: {formatPrice(line.per_server.monthly_ttc)} incl. VAT
                        </div>
                      )}
                    </Td>
                    <Td isActionCell>
                      <Flex spaceItems={{ default: 'spaceItemsNone' }} flexWrap={{ default: 'nowrap' }}>
                        <FlexItem>
                          <RowAction
                            label={`Edit ${name}`}
                            icon={<PencilAltIcon />}
                            onClick={() => onEditLine(kind, line)}
                            isDisabled={isBusy}
                          />
                        </FlexItem>
                        <FlexItem>
                          <RowAction
                            label={`Remove ${name}`}
                            icon={<TrashIcon />}
                            onClick={() => onRemoveLine(line)}
                            isDisabled={isBusy}
                          />
                        </FlexItem>
                      </Flex>
                    </Td>
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
        )}
      </section>
    );
  };

  const renderOtherItems = () => (
    <section className="pf-v6-u-mb-xl" aria-labelledby="gpu-budget-section-other">
      <Title headingLevel="h3" size="lg" id="gpu-budget-section-other" className="pf-v6-u-mb-sm">
        {BUDGET_OTHER_ITEMS_TITLE}
      </Title>
      <Content component="p" className="pf-v6-u-text-color-subtle pf-v6-u-mb-sm">
        {BUDGET_OTHER_ITEMS_NOTE}
      </Content>
      <Table aria-label="Other items of the budget" variant="compact">
        <Thead>
          <Tr>
            <Th>Item</Th>
            <Th modifier="nowrap">Quantity</Th>
            <Th screenReaderText="Actions" />
          </Tr>
        </Thead>
        <Tbody>
          {otherLines.map((line) => (
            <Tr key={line.id}>
              <Td dataLabel="Item" className="pf-v6-u-text-color-subtle">
                <ProductCell line={{ ...line, status: 'unavailable' }} warning={lineWarning(budget, line)} />
              </Td>
              <Td dataLabel="Quantity" modifier="nowrap" className="pf-v6-u-text-color-subtle">
                {formatCount(line.quantity)}
              </Td>
              <Td isActionCell>
                <RowAction
                  label={`Remove ${lineName(line)}`}
                  icon={<TrashIcon />}
                  onClick={() => onRemoveLine(line)}
                  isDisabled={isBusy}
                />
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
    </section>
  );

  return (
    <>
      {catalog.kinds.map(renderSection)}
      {otherLines.length > 0 && renderOtherItems()}
      <GpuBudgetTotalsCard budget={budget} catalog={catalog} />
    </>
  );
};

export default GpuBudgetEditor;
